<template>
  <header class="main-header" :class="{ 'main-header-auth': isAuthenticated }">
    <template v-if="isAuthenticated">
      <MobileMenu :social="social" :user="rootStore.user" />

      <aside class="app-sidebar d-none d-lg-flex">
        <router-link class="app-sidebar__brand" :to="brandLink" @click="handleBrandClick">
          <img src="@/assets/images/infodiagram-account.svg" alt="InfoDiagram" />
        </router-link>
        <div class="app-sidebar__panel">
          <Navigation />
        </div>
        <div class="app-sidebar__footer">
          <span>Built for faster slide discovery</span>
          <a href="https://infodiagram.com" target="_blank" rel="noreferrer">Visit infodiagram.com</a>
        </div>
      </aside>

      <div class="app-topbar">
        <div class="app-topbar__intro">
          <button class="app-topbar__menu d-lg-none" type="button" aria-label="Open navigation" @click.prevent="toggleSideMenu">
            <svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 1h22M0 8h22M0 15h22" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
          <router-link class="app-topbar__mobile-brand d-lg-none" :to="brandLink" @click="handleBrandClick">
            <img src="@/assets/images/icons/infoDiagram-logo-sygnet.svg" alt="InfoDiagram Logo" />
          </router-link>
          <div>
            <p class="app-topbar__eyebrow">{{ currentEyebrow }}</p>
            <h1>{{ currentTitle }}</h1>
          </div>
        </div>

        <div class="app-topbar__actions" v-if="rootStore.user">
          <div class="app-topbar__credits">
            <span>Credits</span>
            <strong>{{ creditsLabel }}</strong>
          </div>
          <GetCreditsButton />
          <div class="app-topbar__profile">
            <img src="@/assets/images/icons/user.svg" class="user-icon" alt="" />
            <MyProfileDropdown />
          </div>
        </div>
      </div>
    </template>

    <div v-else class="d-flex guest-mobile-header d-md-none align-items-center justify-content-center">
      <router-link to="/" aria-label="Go to login">
        <img src="@/assets/images/icons/infoDiagram-logo-sygnet.svg" alt="InfoDiagram Logo" />
      </router-link>
    </div>
    <div v-if="!isAuthenticated" class="container-fluid header-inner">
      <div class="row">
        <div class="col-md-4 d-none d-md-block">
          <div>
            <router-link :to="brandLink" @click="handleBrandClick">
              <img src="@/assets/images/infodiagram-account.svg" style="display: block; width: 100%" />
            </router-link>
          </div>
        </div>
        <div class="col-md-8 d-none d-md-block">
          <div v-if="rootStore.user" class="row align-items-center justify-content-end d-flex user-nav">
            <div class="user-nav-item">
              <div class="d-flex align-items-center">
                <img src="@/assets/images/icons/user.svg" class="user-icon" />
                <MyProfileDropdown v-if="rootStore.user" />
              </div>
            </div>
            <div class="user-nav-item">
              <MyCredits v-if="rootStore.user" />
            </div>
            <div>
              <GetCreditsButton v-if="rootStore.user" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useAuthStore } from '@/stores/useAuthStore'
import { useRootStore } from '@/stores/useRootStore'
import { useFilterStore } from '@/stores/useFilterStore'
import MobileMenu from '@/components/Layout/MobileMenu.vue'
import Navigation from '@/components/Layout/Navigation.vue'
import MyProfileDropdown from '@/components/Panel/MyProfileDropdown.vue'
import MyCredits from '@/components/Panel/MyCredits.vue'
import GetCreditsButton from '@/components/Buttons/GetCreditsButton.vue'
import { useRoute } from 'vue-router'

const rootStore = useRootStore()
const authStore = useAuthStore()
const filterStore = useFilterStore()
const route = useRoute()
const isAuthenticated = computed(() => authStore.isLogged)
const brandLink = computed(() => isAuthenticated.value ? '/panel/browse' : '/')
const currentTitle = computed(() => {
  if (route.name === 'profile') return 'Personal data'
  if (route.name === 'subscription') return 'Subscription'
  if (filterStore.mode === 'my') return 'My slides'
  if (filterStore.mode === 'favourites') return 'Wishlist'
  return 'Slides library'
})
const currentEyebrow = computed(() => {
  if (route.name === 'profile') return 'Account settings'
  if (route.name === 'subscription') return 'Billing and plans'
  return 'Slide workspace'
})
const creditsLabel = computed(() => {
  if (!rootStore.user) return 'N/A'
  return rootStore.getCreditsAmount
})

const social = [
  { title: 'Contact us', class: 'contact', url: `${import.meta.env.VITE_WEBSITE_URL}contact.html`, target: false },
  { title: 'Our Sideshare presentations', class: 'slideshare', url: 'http://www.slideshare.net/infodiagram', target: '_blank' },
  { title: 'Follow us on Twitter', class: 'twitter', url: 'https://twitter.com/infodiagram', target: '_blank' },
  { title: 'Our Linkedin profile', class: 'linkedin', url: 'http://www.linkedin.com/company/infodiagram-com', target: '_blank' },
  { title: 'Check out pins we love', class: 'pinterest', url: 'http://www.pinterest.com/infodiagram/', target: '_blank' }
]

function toggleSideMenu() {
  rootStore.toggleMobileMenu()
  if (rootStore.mobileMenuActive) {
    document.body.classList.add('mobile-nav-open')
  } else {
    document.body.classList.remove('mobile-nav-open')
  }
}

function handleBrandClick() {
  if (isAuthenticated.value) {
    filterStore.clearFiltering()
  }
}
</script>

<style lang="scss">
@use "sass:color";
.main-header {
  width: 100%; border-top: 2px solid #d9d9d9;
  .header-inner { padding-top: 14px; padding-bottom: 11px; }
  .user-icon { width: 30px; height: 30px; }
  .user-nav { flex: 1 1 auto; & > * { width: auto; max-width: auto; } }
  .user-nav-item, .user-nav-item .btn { font-size: 20px; }
  .get-credits { width: 100%; justify-content: center; min-width: 242px; }
}

.main-header-auth {
  border-top: 0;
}

.app-sidebar {
  position: fixed;
  left: 20px;
  top: 20px;
  bottom: 20px;
  z-index: 20;
  width: calc(var(--app-shell-sidebar-width) - 20px);
  flex-direction: column;
  gap: 22px;
  padding: 22px 18px 18px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top, rgba(247, 153, 57, 0.22), transparent 26%),
    linear-gradient(180deg, #6c2c68 0%, #4b2755 40%, #221833 100%);
  box-shadow: 0 30px 60px rgba(52, 21, 62, 0.22);
  &__brand {
    display: block;
    padding: 8px 10px 2px;
    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }
  &__panel {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
    &::-webkit-scrollbar {
      width: 4px;
      height: 0;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.15);
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: rgba(255, 255, 255, 0.25);
    }
  }
  &__footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 14px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.58);
    font-size: 12px;
    a {
      color: #ffbf79;
      text-decoration: none;
    }
  }
}

.app-topbar {
  position: fixed;
  top: 20px;
  right: 24px;
  left: calc(var(--app-shell-sidebar-width) + 24px);
  z-index: 18;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  min-height: var(--app-shell-topbar-height);
  padding: 16px 22px;
  border: 1px solid rgba(145, 61, 136, 0.08);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
  box-shadow: 0 14px 36px rgba(70, 36, 89, 0.08);
  &__intro {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
    h1 {
      margin: 0;
      font-size: 28px;
      line-height: 1.1;
      color: #2d2138;
    }
  }
  &__eyebrow {
    margin: 0 0 6px;
    font-size: 11px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #8c7a8f;
  }
  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
  &__credits {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 132px;
    padding: 10px 14px;
    border-radius: 18px;
    background: linear-gradient(135deg, rgba(247, 153, 57, 0.14), rgba(145, 61, 136, 0.08));
    span {
      font-size: 11px;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: #8a6d52;
    }
    strong {
      display: inline-block;
      color: #2d2138;
      font-size: 16px;
    }
  }
  &__profile {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 18px;
    background: #fff;
    box-shadow: inset 0 0 0 1px rgba(145, 61, 136, 0.1);
  }
  &__menu {
    width: 44px;
    height: 44px;
    border: 0;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(247, 153, 57, 0.18), rgba(145, 61, 136, 0.12));
    color: #5e325e;
  }
  &__mobile-brand {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.82);
    box-shadow: inset 0 0 0 1px rgba(145, 61, 136, 0.1);
    img {
      width: 22px;
      height: 22px;
    }
  }
}

@media screen and (max-width: 991px) {
  .main-header {
    border: none;
  }
  .guest-mobile-header {
    min-height: 62px;
    a {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    img {
      display: block;
      width: 28px;
      height: 28px;
      margin: 0;
    }
  }
  .main-header-auth {
    position: static !important;
    height: auto;
    background: transparent;
    box-shadow: none;
  }
  .app-topbar {
    top: 12px;
    left: 16px;
    right: 16px;
    min-height: var(--app-shell-topbar-height);
    padding: 12px 14px;
    border-radius: 22px;
    gap: 14px;
    &__intro {
      gap: 12px;
      h1 {
        font-size: 20px;
      }
    }
    &__eyebrow {
      margin-bottom: 4px;
      font-size: 10px;
    }
    &__actions {
      display: none;
    }
  }
}
</style>
